import {
  AdvancedAssignmentFilterOption,
  AssignmentDeviationEnum,
  AssignmentSortFieldEnum,
  AssignmentStatusEnum,
  AssignmentViewModel,
  CaseTypeEnum,
  DirectionEnum,
} from 'api/model';
import { useCallback, useMemo, useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';
import {
  faArrowRight,
  faCar,
  faCaretLeft,
  faCaretRight,
  faCheck,
  faCircle,
  faExclamationTriangle,
  faFilter,
  faUserAlt,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ProgressBarWithTooltip } from 'pages/Employees/DrivingPolicy';
import SendToFieldTestersButton from 'components/filterBar/SendToFieldTestersButton';
import Button from 'components/inputs/Button';
import Input from 'components/inputs/Input';
import LabelWrap from 'components/inputs/LabelWrap';
import MultiSelect, { Option } from 'components/inputs/MultiSelect';
import Dropdown from 'components/Dropdown';
import BusinessUnitSelect from 'components/inputs/BusinessUnitSelect';
import AssignmentProgressRing from 'components/AssignmentProgressRing';
import AssignmentsList, {
  ServerSortDescriptor,
} from 'components/AssignmentsList';
import Table from 'components/Table';
import { ColumnSetting } from 'components/Table/utils';
import LoadingSpinner from 'components/spinners/LoadingSpinner';
import useTranslations from 'contexts/basicData/useTranslations';
import useBusinessUnits from 'contexts/basicData/useBusinessUnits';
import useModalStack from 'contexts/modal/useModalStack';
import { TableIdentifierEnum } from 'contexts/basicData/useActiveColumns';
import { useDayPlanningGetOverview } from 'api/day-planning/day-planning';
import type { DayPlanningFieldTesterDTO } from 'api/model/dayPlanningFieldTesterDTO';
import {
  FilterButton,
  FilterPickerContainer,
} from 'components/filterBar/FilterPicker';
import { useLocalStorage } from 'usehooks-ts';
import Routes from 'constants/Routes';
import {
  DEFAULT_MAX_HOURS_PER_4_WEEKS,
  DEFAULT_MAX_HOURS_PER_WEEK,
} from 'constants/AppConstants';
import { getDrivingBarHours } from 'pages/Employees/Availability/availability-helper';
import DayPlanningDetailModal from './DayPlanningDetailModal';
import useDayPlanningAssignments from './useDayPlanningAssignments';

// --- Date helpers ---

function addDays(date: Date, n: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + n);
  return result;
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function getMondayOfWeek(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay(); // 0=Sun, 1=Mon, ...
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d;
}

function getISOWeekNumber(date: Date): number {
  const d = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
  );
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

// --- Styled components ---

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px;
  gap: 10px;
`;

const TopBar = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.primary};
`;

const WeekSelectorContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ArrowButton = styled.button`
  background: none;
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: 4px;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.foreground.primary};
  transition: all 0.15s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.background.secondary};
  }
`;

const WeekLabel = styled.span`
  font-size: 0.85rem;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.foreground.tint};
  white-space: nowrap;
  padding: 0 4px;
  min-width: 32px;
  text-align: center;
`;

const DayBtn = styled.button<{ isSelected?: boolean; isToday?: boolean }>`
  background: none;
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: 4px;
  cursor: pointer;
  padding: 3px 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
  line-height: 1.2;
  transition: all 0.15s ease;
  min-width: 45px;

  &:hover {
    background-color: ${({ theme }) => theme.colors.background.secondary};
  }

  ${({ isSelected, theme }) =>
    isSelected &&
    css`
      background-color: ${theme.colors.foreground.tint};
      color: white;
      border-color: ${theme.colors.foreground.tint};

      &:hover {
        background-color: ${theme.colors.foreground.tint};
      }
    `}

  ${({ isToday, isSelected, theme }) =>
    isToday &&
    !isSelected &&
    css`
      border-color: ${theme.colors.foreground.tint};
    `}
`;

const DayName = styled.span`
  font-size: 0.75rem;
  text-transform: capitalize;
`;

const DayNumber = styled.span`
  font-size: 0.85rem;
  font-weight: bold;
`;

const SummaryBar = styled.div`
  display: flex;
  flex-direction: row;
  gap: 20px;
  padding: 10px;
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: 6px;
  font-size: 0.95rem;
`;

const SummaryItem = styled.div<{ highlight?: 'good' | 'warn' | 'bad' }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 4px 12px;

  .value {
    font-size: 1.4rem;
    font-weight: bold;
    color: ${({ theme, highlight }) =>
      highlight === 'good'
        ? theme.colors.foreground.good
        : highlight === 'bad'
          ? theme.colors.foreground.error
          : highlight === 'warn'
            ? theme.colors.foreground.warning
            : theme.colors.foreground.primary};
  }

  .label {
    font-size: 0.8rem;
    color: ${({ theme }) => theme.colors.foreground.tint};
  }
`;

const TabBar = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.primary};
`;

const Tab = styled.button<{ active?: boolean }>`
  padding: 8px 20px;
  border: none;
  border-bottom: 2px solid transparent;
  background: transparent;
  color: ${({ theme }) => theme.colors.foreground.primary};
  font: inherit;
  font-size: 1rem;
  cursor: pointer;

  ${({ active, theme }) =>
    active &&
    css`
      border-bottom: 2px solid ${theme.colors.foreground.tint};
      font-weight: bold;
    `}

  &:hover {
    color: ${({ theme }) => theme.colors.foreground.tint};
  }
`;

const FilterBar = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  padding: 5px 0;
  flex-wrap: wrap;
`;

const StatusIcon = styled.span<{ color: string }>`
  color: ${({ color }) => color};
  margin-right: 4px;
`;

const ClickableRow = styled.span`
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;

  &:hover {
    text-decoration: underline;
  }
`;

const FilterDropdownWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
  padding: 10px;
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
`;

const FilterBadge = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.foreground.button};
  color: ${({ theme }) => theme.colors.background.button};
  font-size: 0.8em;
  font-weight: bold;
`;

const FilterButton2 = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  white-space: nowrap;
  height: 30px;
`;

const HighlightMultiSelect = styled(MultiSelect)`
  ${({ value, theme }) =>
    value.size > 0 &&
    css`
      background: ${theme.colors.background.highlight};
    `}
` as typeof MultiSelect;

const ClickableSummaryItem = styled(SummaryItem)`
  cursor: pointer;
  &:hover {
    opacity: 0.7;
  }
`;

// --- Day planning filter presets ---

const dayPlanningFiltersByLabel = {
  Skapade: {
    statuses: new Set([AssignmentStatusEnum.Created]),
    deviations: new Set([AssignmentDeviationEnum.None]),
  },
  Planerade: {
    statuses: new Set([AssignmentStatusEnum.Planned]),
    deviations: new Set([AssignmentDeviationEnum.None]),
  },
  Tilldelade: {
    statuses: new Set([
      AssignmentStatusEnum.Assigned,
      AssignmentStatusEnum.AssignedToCarTransportTruck,
    ]),
    deviations: new Set([AssignmentDeviationEnum.None]),
  },
  Accepterade: {
    statuses: new Set([AssignmentStatusEnum.Accepted]),
    deviations: new Set([AssignmentDeviationEnum.None]),
  },
  Avbokade: {
    statuses: new Set([
      AssignmentStatusEnum.Canceled,
      AssignmentStatusEnum.Aborted,
      AssignmentStatusEnum.CanceledNotFinished,
      AssignmentStatusEnum.AbortedNotFinished,
    ]),
    deviations: new Set([
      AssignmentDeviationEnum.Canceled,
      AssignmentDeviationEnum.Aborted,
      AssignmentDeviationEnum.None,
    ]),
  },
} as const;

// --- Types ---

type ActiveTab = 'assignments' | 'fieldTesters';

type FTFilter = 'alla' | 'klara' | 'medLuckor' | 'ejFullbokade';

// --- Component ---

const DayPlanningOverview = () => {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState<Date>(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    return tomorrow;
  });

  const [selectedBusinessUnits, setSelectedBusinessUnits] = useLocalStorage<
    number[]
  >('dayPlanning.businessUnits', []);
  const [activeTab, setActiveTab] = useState<ActiveTab>('assignments');
  const translations = useTranslations();
  const { unitsById } = useBusinessUnits();
  const modalStack = useModalStack();

  // --- Assignment tab filter state ---
  const [aStatuses, setAStatuses] = useState<AssignmentStatusEnum[]>([]);
  const [aDeviations, setADeviations] = useState<AssignmentDeviationEnum[]>([]);
  const [aCaseTypes, setACaseTypes] = useState<CaseTypeEnum[]>([]);
  const [aSearchText, setASearchText] = useState('');
  const [aAdvanced, setAAdvanced] = useState<AdvancedAssignmentFilterOption[]>(
    []
  );
  const [aFiltersOpen, setAFiltersOpen] = useState(false);

  // --- Server sort state ---
  const [serverSort, setServerSort] = useState<ServerSortDescriptor | null>(
    null
  );

  // --- FT tab filter state ---
  const [ftSearchText, setFtSearchText] = useState('');
  const [ftFilter, setFtFilter] = useState<FTFilter>('alla');
  const [ftCountyFilter, setFtCountyFilter] = useState<Set<string>>(new Set());
  const [ftTeamFilter, setFtTeamFilter] = useState<Set<string>>(new Set());
  const [ftDeptFilter, setFtDeptFilter] = useState<Set<string>>(new Set());
  const [ftFiltersOpen, setFtFiltersOpen] = useState(false);

  // --- Week day selector memos ---
  const today = useMemo(() => new Date(), []);
  const monday = useMemo(() => getMondayOfWeek(selectedDate), [selectedDate]);
  const weekNumber = useMemo(
    () => getISOWeekNumber(selectedDate),
    [selectedDate]
  );
  const weekDays = useMemo(() => {
    const days: Date[] = [];
    for (let i = 0; i < 7; i += 1) {
      days.push(addDays(monday, i));
    }
    return days;
  }, [monday]);

  // --- Data fetching ---

  const { data, isLoading, refetch } = useDayPlanningGetOverview(
    {
      date: selectedDate,
      businessUnitIDs:
        selectedBusinessUnits.length > 0 ? selectedBusinessUnits : undefined,
    },
    {
      query: {
        refetchInterval: 30000,
      },
    }
  );

  const {
    assignments,
    loading: assignmentsLoading,
    currentPage,
    totalPageCount,
    pageSize,
    goToPage,
    refetch: refetchAssignments,
  } = useDayPlanningAssignments({
    date: selectedDate,
    businessUnits: selectedBusinessUnits,
    caseTypes: aCaseTypes.length > 0 ? aCaseTypes : undefined,
    assignmentStatuses: aStatuses.length > 0 ? aStatuses : undefined,
    assignmentDeviations: aDeviations.length > 0 ? aDeviations : undefined,
    advancedFilters: aAdvanced.length > 0 ? aAdvanced : undefined,
    searchText: aSearchText || undefined,
    orderByField: serverSort?.orderByField as
      | AssignmentSortFieldEnum
      | undefined,
    direction: serverSort?.direction as DirectionEnum | undefined,
  });

  // --- Assignment filter helpers ---

  const isPresetActive = useCallback(
    (presetStatuses: Set<AssignmentStatusEnum>) => {
      if (aStatuses.length !== presetStatuses.size) return false;
      return aStatuses.every((s) => presetStatuses.has(s));
    },
    [aStatuses]
  );

  const handlePresetClick = useCallback(
    (
      statuses: Set<AssignmentStatusEnum>,
      deviations: Set<AssignmentDeviationEnum>
    ) => {
      setAStatuses(Array.from(statuses));
      setADeviations(Array.from(deviations));
    },
    []
  );

  const caseTypeOptions = useMemo(
    (): Option<CaseTypeEnum>[] =>
      Object.entries(translations.caseTypes).map(([type, name]) => ({
        label: name as string,
        value: parseInt(type, 10) as CaseTypeEnum,
      })),
    [translations.caseTypes]
  );

  const assignmentStatusOptions = useMemo(
    (): Option<AssignmentStatusEnum>[] =>
      Object.entries(translations.assignmentStatuses).map(([status, name]) => ({
        label: name as string,
        value: parseInt(status, 10) as AssignmentStatusEnum,
      })),
    [translations.assignmentStatuses]
  );

  const businessUnitOptions = useMemo(
    (): Option<number>[] =>
      Array.from(unitsById.values()).map((unit) => ({
        label: unit.name,
        value: unit.id,
      })),
    [unitsById]
  );

  const advancedFilterOptions =
    useMemo((): Option<AdvancedAssignmentFilterOption>[] => {
      const supported = new Set<number>([
        AdvancedAssignmentFilterOption.HasAssignee,
        AdvancedAssignmentFilterOption.FromStrangnasLogistik,
        AdvancedAssignmentFilterOption.ToStrangnasLogistik,
        AdvancedAssignmentFilterOption.IncludePlaceholderAndTransportAssignments,
      ]);
      return Object.entries(translations.advancedAssignmentFilterOptions ?? {})
        .map(([key, name]) => ({
          label: name as string,
          value: parseInt(key, 10) as AdvancedAssignmentFilterOption,
        }))
        .filter((opt) => supported.has(opt.value));
    }, [translations.advancedAssignmentFilterOptions]);

  const aFilterCount = useMemo(
    () => aCaseTypes.length + aStatuses.length + aAdvanced.length,
    [aCaseTypes.length, aStatuses.length, aAdvanced.length]
  );

  // --- FT filter options derived from data ---

  const ftCountyOptions = useMemo((): Option<string>[] => {
    if (!data?.fieldTesters) return [];
    const counties = new Set(
      data.fieldTesters.map((ft) => ft.county).filter((c): c is string => !!c)
    );
    return Array.from(counties)
      .sort()
      .map((c) => ({ label: c, value: c }));
  }, [data?.fieldTesters]);

  const ftTeamOptions = useMemo((): Option<string>[] => {
    if (!data?.fieldTesters) return [];
    const teams = new Set(
      data.fieldTesters.map((ft) => ft.teamName).filter((t): t is string => !!t)
    );
    return Array.from(teams)
      .sort()
      .map((t) => ({ label: t, value: t }));
  }, [data?.fieldTesters]);

  const ftDeptOptions = useMemo((): Option<string>[] => {
    if (!data?.fieldTesters) return [];
    const depts = new Set(
      data.fieldTesters
        .map((ft) => ft.departmentName)
        .filter((d): d is string => !!d)
    );
    return Array.from(depts)
      .sort()
      .map((d) => ({ label: d, value: d }));
  }, [data?.fieldTesters]);

  const ftFilterCount = useMemo(
    () => ftCountyFilter.size + ftTeamFilter.size + ftDeptFilter.size,
    [ftCountyFilter.size, ftTeamFilter.size, ftDeptFilter.size]
  );

  // --- FT filtering ---

  const filteredFieldTesters = useMemo(() => {
    if (!data?.fieldTesters) return [];
    let list = data.fieldTesters;

    if (ftFilter === 'klara') {
      list = list.filter((ft) => ft.isFullyBooked && ft.issueCount === 0);
    } else if (ftFilter === 'medLuckor') {
      list = list.filter((ft) => ft.isFullyBooked && ft.issueCount > 0);
    } else if (ftFilter === 'ejFullbokade') {
      list = list.filter((ft) => !ft.isFullyBooked);
    }

    if (ftCountyFilter.size > 0) {
      list = list.filter((ft) => ft.county && ftCountyFilter.has(ft.county));
    }
    if (ftTeamFilter.size > 0) {
      list = list.filter((ft) => ft.teamName && ftTeamFilter.has(ft.teamName));
    }
    if (ftDeptFilter.size > 0) {
      list = list.filter(
        (ft) => ft.departmentName && ftDeptFilter.has(ft.departmentName)
      );
    }

    if (ftSearchText) {
      const lower = ftSearchText.toLowerCase();
      list = list.filter(
        (ft) =>
          ft.name.toLowerCase().includes(lower) ||
          ft.homeCity?.toLowerCase().includes(lower) ||
          ft.county?.toLowerCase().includes(lower) ||
          ft.area?.toLowerCase().includes(lower) ||
          ft.teamName?.toLowerCase().includes(lower) ||
          ft.departmentName?.toLowerCase().includes(lower)
      );
    }

    return list;
  }, [
    data?.fieldTesters,
    ftFilter,
    ftSearchText,
    ftCountyFilter,
    ftTeamFilter,
    ftDeptFilter,
  ]);

  const summary = data?.assignmentSummary;

  const fullyBookedCount = useMemo(
    () =>
      data?.fieldTesters?.filter(
        (ft) => ft.isFullyBooked && ft.issueCount === 0
      ).length ?? 0,
    [data?.fieldTesters]
  );
  const bookedWithIssuesCount = useMemo(
    () =>
      data?.fieldTesters?.filter((ft) => ft.isFullyBooked && ft.issueCount > 0)
        .length ?? 0,
    [data?.fieldTesters]
  );
  const totalFTs = data?.fieldTesters?.length ?? 0;
  const remainingFTs = totalFTs - fullyBookedCount - bookedWithIssuesCount;

  // --- Handlers ---

  const handleServerSortChange = useCallback(
    (sort: ServerSortDescriptor | null) => {
      setServerSort(sort);
    },
    []
  );

  const handleSummaryClick = useCallback(
    (
      target:
        | 'alla'
        | 'tilldelade'
        | 'planerade'
        | 'otilldelade'
        | 'avbokade'
        | 'ftKlara'
        | 'ftMedLuckor'
        | 'ftKvar'
        | 'ftAlla'
    ) => {
      if (
        target === 'ftKlara' ||
        target === 'ftMedLuckor' ||
        target === 'ftKvar' ||
        target === 'ftAlla'
      ) {
        setActiveTab('fieldTesters');
        const ftMap: Record<string, FTFilter> = {
          ftKlara: 'klara',
          ftMedLuckor: 'medLuckor',
          ftKvar: 'ejFullbokade',
          ftAlla: 'alla',
        };
        setFtFilter(ftMap[target]);
      } else {
        setActiveTab('assignments');
        if (target === 'alla') {
          setAStatuses([]);
          setADeviations([]);
        } else {
          const preset = {
            tilldelade: dayPlanningFiltersByLabel.Tilldelade,
            planerade: dayPlanningFiltersByLabel.Planerade,
            otilldelade: dayPlanningFiltersByLabel.Skapade,
            avbokade: dayPlanningFiltersByLabel.Avbokade,
          }[target];
          setAStatuses(Array.from(preset.statuses));
          setADeviations(Array.from(preset.deviations));
        }
      }
    },
    []
  );

  const handleOpenDetail = useCallback(
    (ft: DayPlanningFieldTesterDTO) => {
      modalStack.push(
        <DayPlanningDetailModal
          userId={ft.userId}
          userName={ft.name}
          date={selectedDate}
          onClose={() => {
            modalStack.pop();
            refetch();
          }}
        />
      );
    },
    [modalStack, selectedDate, refetch]
  );

  const handleAssignmentItemClick = useCallback(
    (model: AssignmentViewModel) => {
      navigate(
        generatePath(Routes.cases.case, {
          id: String(model.case.caseID),
        })
      );
    },
    [navigate]
  );

  // --- FT table columns ---

  const ftColumnSettings: ColumnSetting<DayPlanningFieldTesterDTO, any>[] =
    useMemo(
      () => [
        {
          head: '#',
          render: (_row, _props, _focused, rowIndex) => rowIndex + 1,
          width: 30,
        },
        {
          head: '',
          render: (row) => {
            if (row.isFullyBooked && row.issueCount === 0) {
              return (
                <StatusIcon color="green">
                  <FontAwesomeIcon icon={faCheck} title="Fullbokad" />
                </StatusIcon>
              );
            }
            if (row.issueCount > 0) {
              return (
                <StatusIcon color="orange">
                  <FontAwesomeIcon
                    icon={faExclamationTriangle}
                    title={
                      row.isFullyBooked
                        ? 'Fullbokad men har luckor'
                        : 'Har luckor'
                    }
                  />
                </StatusIcon>
              );
            }
            return (
              <StatusIcon color="#ccc">
                <FontAwesomeIcon icon={faCircle} title="Ej fullbokad" />
              </StatusIcon>
            );
          },
          width: 20,
        },
        {
          head: 'Län',
          render: (row) => row.county ?? '-',
          width: 100,
          sortFunction: (a, b) =>
            (a.county ?? '').localeCompare(b.county ?? ''),
        },
        {
          head: 'Område',
          render: (row) => row.area ?? '-',
          width: 100,
          sortFunction: (a, b) => (a.area ?? '').localeCompare(b.area ?? ''),
        },
        {
          head: 'Grupp',
          render: (row) => row.teamName ?? '-',
          width: 120,
          sortFunction: (a, b) =>
            (a.teamName ?? '').localeCompare(b.teamName ?? ''),
        },
        {
          head: 'Avdelning',
          render: (row) => row.departmentName ?? '-',
          width: 120,
          sortFunction: (a, b) =>
            (a.departmentName ?? '').localeCompare(b.departmentName ?? ''),
        },
        {
          head: 'Fälttestare',
          render: (row) => (
            <ClickableRow onClick={() => handleOpenDetail(row)}>
              <strong>{row.name}</strong>
            </ClickableRow>
          ),
          width: 130,
          sortFunction: (a, b) => a.name.localeCompare(b.name),
        },
        {
          head: 'Hemort',
          render: (row) => row.homeCity ?? '-',
          width: 90,
          sortFunction: (a, b) =>
            (a.homeCity ?? '').localeCompare(b.homeCity ?? ''),
        },
        {
          head: 'Heltid',
          render: (row) => (row.isFullTime ? 'Ja' : ''),
          width: 50,
          sortFunction: (a, b) =>
            (a.isFullTime ? 1 : 0) - (b.isFullTime ? 1 : 0),
        },
        {
          head: 'Fullbokad',
          render: (row) => (row.isFullyBooked ? 'Ja' : ''),
          width: 65,
          sortFunction: (a, b) =>
            (a.isFullyBooked ? 1 : 0) - (b.isFullyBooked ? 1 : 0),
        },
        {
          head: 'Tillgänglighet',
          render: (row) =>
            row.availabilityStart && row.availabilityEnd
              ? `${row.availabilityStart} - ${row.availabilityEnd}`
              : '-',
          width: 110,
          sortFunction: (a, b) =>
            (a.availabilityStart ?? '').localeCompare(
              b.availabilityStart ?? ''
            ),
        },
        {
          head: 'Uppdrag',
          render: (row) => <strong>{row.assignmentCount}</strong>,
          width: 55,
          sortFunction: (a, b) => a.assignmentCount - b.assignmentCount,
        },
        {
          head: 'Luckor',
          render: (row) => (
            <span style={{ color: row.issueCount > 0 ? 'orange' : 'inherit' }}>
              {row.issueCount}
            </span>
          ),
          width: 50,
          sortFunction: (a, b) => a.issueCount - b.issueCount,
        },
        {
          head: 'Varningar',
          render: (row) =>
            row.dayIncompleteReasons
              .map(
                (reason) =>
                  translations.fieldtestersDayIncomplete[reason] ?? reason
              )
              .join(', ') || '-',
          width: 160,
        },
        {
          head: 'Körtid vecka',
          render: (row) => {
            const value = Math.max(row.drivingHoursThisWeek ?? 0, 0);
            const absenceValue = row.absenceHoursThisWeek ?? 0;
            const maxPerWeek =
              row.maxHoursPerWeek ??
              data?.maxHoursPerWeek ??
              DEFAULT_MAX_HOURS_PER_WEEK;
            return getDrivingBarHours(value, absenceValue, row.isFullTime) ? (
              <ProgressBarWithTooltip
                totalHours={value}
                absenceHours={absenceValue}
                maxHours={maxPerWeek}
                isFullTime={row.isFullTime}
              />
            ) : (
              '-'
            );
          },
          width: 120,
          sortFunction: (a, b) =>
            getDrivingBarHours(
              a.drivingHoursThisWeek,
              a.absenceHoursThisWeek,
              a.isFullTime
            ) -
            getDrivingBarHours(
              b.drivingHoursThisWeek,
              b.absenceHoursThisWeek,
              b.isFullTime
            ),
        },
        {
          head: 'Körtid månad',
          render: (row) => {
            const value = Math.max(row.drivingHoursThisMonth ?? 0, 0);
            const absenceValue = row.absenceHoursThisMonth ?? 0;
            const maxPer4Weeks =
              row.maxHoursPer4Weeks ??
              data?.maxHoursPer4Weeks ??
              DEFAULT_MAX_HOURS_PER_4_WEEKS;
            return getDrivingBarHours(value, absenceValue, row.isFullTime) ? (
              <ProgressBarWithTooltip
                totalHours={value}
                absenceHours={absenceValue}
                maxHours={maxPer4Weeks}
                isFullTime={row.isFullTime}
              />
            ) : (
              '-'
            );
          },
          width: 120,
          sortFunction: (a, b) =>
            getDrivingBarHours(
              a.drivingHoursThisMonth,
              a.absenceHoursThisMonth,
              a.isFullTime
            ) -
            getDrivingBarHours(
              b.drivingHoursThisMonth,
              b.absenceHoursThisMonth,
              b.isFullTime
            ),
        },
        {
          head: 'Körtid 28d',
          render: (row) => {
            const value = Math.max(row.drivingHoursLast28Days ?? 0, 0);
            const absenceValue = row.absenceHoursLast28Days ?? 0;
            const maxPer4Weeks =
              row.maxHoursPer4Weeks ??
              data?.maxHoursPer4Weeks ??
              DEFAULT_MAX_HOURS_PER_4_WEEKS;
            return getDrivingBarHours(value, absenceValue, row.isFullTime) ? (
              <ProgressBarWithTooltip
                totalHours={value}
                absenceHours={absenceValue}
                maxHours={maxPer4Weeks}
                isFullTime={row.isFullTime}
              />
            ) : (
              '-'
            );
          },
          width: 120,
          sortFunction: (a, b) =>
            getDrivingBarHours(
              a.drivingHoursLast28Days,
              a.absenceHoursLast28Days,
              a.isFullTime
            ) -
            getDrivingBarHours(
              b.drivingHoursLast28Days,
              b.absenceHoursLast28Days,
              b.isFullTime
            ),
        },
        {
          head: 'Status',
          render: (row) => {
            if (row.isFullyBooked && row.issueCount === 0) {
              return <span style={{ color: 'green' }}>Fullbokad</span>;
            }
            if (row.isFullyBooked && row.issueCount > 0) {
              return (
                <span style={{ color: 'orange' }}>
                  Fullbokad ({row.issueCount} luckor)
                </span>
              );
            }
            if (row.issueCount > 0) {
              return (
                <span style={{ color: 'orange' }}>
                  Har {row.issueCount} luckor
                </span>
              );
            }
            return <span style={{ color: '#999' }}>Ej fullbokad</span>;
          },
          width: 110,
          sortFunction: (a, b) => {
            if (a.isFullyBooked !== b.isFullyBooked)
              return a.isFullyBooked ? 1 : -1;
            return b.issueCount - a.issueCount;
          },
        },
        {
          head: '',
          render: (row) => (
            <ClickableRow onClick={() => handleOpenDetail(row)}>
              Planera <FontAwesomeIcon icon={faArrowRight} />
            </ClickableRow>
          ),
          width: 60,
        },
      ],
      [
        handleOpenDetail,
        translations.fieldtestersDayIncomplete,
        data?.maxHoursPerWeek,
        data?.maxHoursPer4Weeks,
      ]
    );

  // --- Render ---

  return (
    <Container>
      <TopBar>
        <WeekSelectorContainer>
          <ArrowButton
            onClick={() => setSelectedDate((d) => addDays(d, -1))}
            title="Föregående dag"
          >
            <FontAwesomeIcon icon={faCaretLeft} />
          </ArrowButton>
          <WeekLabel>v.{weekNumber}</WeekLabel>
          {weekDays.map((day) => {
            const selected = isSameDay(day, selectedDate);
            const isToday = isSameDay(day, today);
            return (
              <DayBtn
                key={day.toISOString()}
                isSelected={selected}
                isToday={isToday}
                onClick={() => setSelectedDate(day)}
              >
                <DayName>
                  {day.toLocaleDateString('sv-SE', { weekday: 'short' })}
                </DayName>
                <DayNumber>
                  {day.getDate()}/{day.getMonth() + 1}
                </DayNumber>
              </DayBtn>
            );
          })}
          <ArrowButton
            onClick={() => setSelectedDate((d) => addDays(d, 1))}
            title="Nästa dag"
          >
            <FontAwesomeIcon icon={faCaretRight} />
          </ArrowButton>
        </WeekSelectorContainer>

        <LabelWrap label="Enheter">
          <BusinessUnitSelect
            onChange={(units) => setSelectedBusinessUnits(Array.from(units))}
            value={new Set(selectedBusinessUnits)}
          />
        </LabelWrap>
      </TopBar>

      {isLoading ? (
        <LoadingSpinner>Laddar dagsplanering...</LoadingSpinner>
      ) : (
        <>
          <SummaryBar>
            <AssignmentProgressRing
              totalAssignments={summary?.totalAssignments ?? 0}
              assignedCount={
                (summary?.totalAssignments ?? 0) -
                (summary?.unplannedCount ?? 0)
              }
              plannedCount={0}
              icon={faCar}
            />
            <SummaryItem>
              <span className="value">{summary?.uniqueCasesCount ?? 0}</span>
              <span className="label">Totalt ärenden</span>
            </SummaryItem>
            <ClickableSummaryItem onClick={() => handleSummaryClick('alla')}>
              <span className="value">{summary?.totalAssignments ?? 0}</span>
              <span className="label">Totalt uppdrag</span>
            </ClickableSummaryItem>
            <ClickableSummaryItem
              highlight="good"
              onClick={() => handleSummaryClick('tilldelade')}
            >
              <span className="value">
                {(summary?.totalAssignments ?? 0) -
                  (summary?.unplannedCount ?? 0) -
                  (summary?.plannedCount ?? 0)}
              </span>
              <span className="label">Tilldelade</span>
            </ClickableSummaryItem>
            <ClickableSummaryItem
              highlight="warn"
              onClick={() => handleSummaryClick('planerade')}
            >
              <span className="value">{summary?.plannedCount ?? 0}</span>
              <span className="label">Planerade (ej skickade)</span>
            </ClickableSummaryItem>
            <ClickableSummaryItem
              highlight="bad"
              onClick={() => handleSummaryClick('otilldelade')}
            >
              <span className="value">{summary?.unplannedCount ?? 0}</span>
              <span className="label">Otilldelade</span>
            </ClickableSummaryItem>
            <ClickableSummaryItem
              highlight="bad"
              onClick={() => handleSummaryClick('avbokade')}
            >
              <span className="value">{summary?.cancelledCount ?? 0}</span>
              <span className="label">Avbokade</span>
            </ClickableSummaryItem>
            <div
              style={{
                marginLeft: 'auto',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <SendToFieldTestersButton
                dateRange={[selectedDate, selectedDate]}
                excludeCaseTypes={[CaseTypeEnum.TestDrive]}
              />
            </div>
            <ClickableSummaryItem
              onClick={() => handleSummaryClick('ftKlara')}
              style={{
                borderLeft: '1px solid #ddd',
                paddingLeft: 20,
              }}
            >
              <span className="value" style={{ color: 'green' }}>
                {fullyBookedCount}
              </span>
              <span className="label">FT klara</span>
            </ClickableSummaryItem>
            <ClickableSummaryItem
              highlight="warn"
              onClick={() => handleSummaryClick('ftMedLuckor')}
            >
              <span className="value">{bookedWithIssuesCount}</span>
              <span className="label">FT med luckor</span>
            </ClickableSummaryItem>
            <ClickableSummaryItem
              highlight="bad"
              onClick={() => handleSummaryClick('ftKvar')}
            >
              <span className="value">{remainingFTs}</span>
              <span className="label">FT kvar</span>
            </ClickableSummaryItem>
            <div
              style={{
                borderLeft: '1px solid #ddd',
                paddingLeft: 12,
                display: 'flex',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <SummaryItem>
                <span className="value">
                  {fullyBookedCount + bookedWithIssuesCount}
                </span>
                <span className="label">FT i planering</span>
              </SummaryItem>
              <ClickableSummaryItem onClick={() => handleSummaryClick('ftAlla')}>
                <span className="value">{totalFTs}</span>
                <span className="label">Tillgängliga FT</span>
              </ClickableSummaryItem>
              <AssignmentProgressRing
                totalAssignments={totalFTs}
                assignedCount={fullyBookedCount}
                plannedCount={bookedWithIssuesCount}
                tooltip={`${fullyBookedCount + bookedWithIssuesCount} av ${totalFTs} tillgängliga FT har planerats`}
                emptyTooltip="Inga tillgängliga FT"
                icon={faUserAlt}
              />
            </div>
          </SummaryBar>

          <TabBar>
            <Tab
              active={activeTab === 'assignments'}
              onClick={() => setActiveTab('assignments')}
            >
              Uppdrag
            </Tab>
            <Tab
              active={activeTab === 'fieldTesters'}
              onClick={() => setActiveTab('fieldTesters')}
            >
              Fälttestare
            </Tab>
          </TabBar>

          {activeTab === 'assignments' && (
            <>
              <FilterBar>
                <FilterPickerContainer>
                  <FilterButton
                    onClick={() => {
                      setAStatuses([]);
                      setADeviations([]);
                    }}
                    selected={aStatuses.length === 0}
                  >
                    Alla
                  </FilterButton>
                  {Object.entries(dayPlanningFiltersByLabel).map(
                    ([label, { statuses, deviations }]) => (
                      <FilterButton
                        key={label}
                        onClick={() => handlePresetClick(statuses, deviations)}
                        selected={isPresetActive(statuses)}
                        type="button"
                      >
                        {label}
                      </FilterButton>
                    )
                  )}
                </FilterPickerContainer>

                <Input
                  onChange={(e) => setASearchText(e.target.value)}
                  placeholder="Sök regnr, adress, FT..."
                  value={aSearchText}
                  width={220}
                />

                <Dropdown
                  onLostFocus={() => setAFiltersOpen(false)}
                  position="left"
                  content={
                    aFiltersOpen && (
                      <FilterDropdownWrapper>
                        <LabelWrap label="Ärendetyp">
                          <HighlightMultiSelect
                            onChange={(newVal) =>
                              setACaseTypes(Array.from(newVal))
                            }
                            options={caseTypeOptions}
                            value={new Set(aCaseTypes)}
                          />
                        </LabelWrap>
                        <LabelWrap label="Uppdragsstatus">
                          <HighlightMultiSelect
                            onChange={(newVal) =>
                              setAStatuses(Array.from(newVal))
                            }
                            options={assignmentStatusOptions}
                            value={new Set(aStatuses)}
                          />
                        </LabelWrap>
                        <LabelWrap label="Enhet">
                          <HighlightMultiSelect
                            onChange={(newVal) =>
                              setSelectedBusinessUnits(Array.from(newVal))
                            }
                            options={businessUnitOptions}
                            value={new Set(selectedBusinessUnits)}
                          />
                        </LabelWrap>
                        <LabelWrap label="Avancerat">
                          <HighlightMultiSelect
                            onChange={(newVal) =>
                              setAAdvanced(Array.from(newVal))
                            }
                            options={advancedFilterOptions}
                            value={new Set(aAdvanced)}
                          />
                        </LabelWrap>
                        <FilterButton2
                          disabled={aFilterCount === 0}
                          onClick={() => {
                            setACaseTypes([]);
                            setAStatuses([]);
                            setADeviations([]);
                            setAAdvanced([]);
                          }}
                        >
                          Rensa
                        </FilterButton2>
                      </FilterDropdownWrapper>
                    )
                  }
                >
                  <FilterButton2
                    icon={<FontAwesomeIcon icon={faFilter} />}
                    onClick={() => setAFiltersOpen(!aFiltersOpen)}
                  >
                    <span>Filter</span>
                    {aFilterCount > 0 && (
                      <FilterBadge>{aFilterCount}</FilterBadge>
                    )}
                  </FilterButton2>
                </Dropdown>
              </FilterBar>

              <AssignmentsList
                tableIdentifier={
                  TableIdentifierEnum.DayPlanningAssignmentsTable
                }
                isDayPlanningView
                assignments={assignments}
                loading={assignmentsLoading}
                showStatusColors
                onAssignmentItemClick={handleAssignmentItemClick}
                onAssignmentUpdated={async () => {
                  await Promise.all([refetchAssignments(), refetch()]);
                }}
                currentPage={currentPage}
                onGoToPage={goToPage}
                totalPageCount={totalPageCount}
                pageSize={pageSize}
                onServerSortChange={handleServerSortChange}
              />
            </>
          )}

          {activeTab === 'fieldTesters' && (
            <>
              <FilterBar>
                <FilterPickerContainer>
                  <FilterButton
                    onClick={() => setFtFilter('alla')}
                    selected={ftFilter === 'alla'}
                  >
                    Alla
                  </FilterButton>
                  <FilterButton
                    onClick={() => setFtFilter('klara')}
                    selected={ftFilter === 'klara'}
                  >
                    Klara
                  </FilterButton>
                  <FilterButton
                    onClick={() => setFtFilter('medLuckor')}
                    selected={ftFilter === 'medLuckor'}
                  >
                    Med luckor
                  </FilterButton>
                  <FilterButton
                    onClick={() => setFtFilter('ejFullbokade')}
                    selected={ftFilter === 'ejFullbokade'}
                  >
                    Ej fullbokade
                  </FilterButton>
                </FilterPickerContainer>

                <Input
                  onChange={(e) => setFtSearchText(e.target.value)}
                  placeholder="Sök namn, ort, län..."
                  value={ftSearchText}
                  width={220}
                />

                <Dropdown
                  onLostFocus={() => setFtFiltersOpen(false)}
                  position="left"
                  content={
                    ftFiltersOpen && (
                      <FilterDropdownWrapper>
                        <LabelWrap label="Län">
                          <HighlightMultiSelect
                            onChange={(newVal) => setFtCountyFilter(newVal)}
                            options={ftCountyOptions}
                            value={ftCountyFilter}
                          />
                        </LabelWrap>
                        <LabelWrap label="Grupp">
                          <HighlightMultiSelect
                            onChange={(newVal) => setFtTeamFilter(newVal)}
                            options={ftTeamOptions}
                            value={ftTeamFilter}
                          />
                        </LabelWrap>
                        <LabelWrap label="Avdelning">
                          <HighlightMultiSelect
                            onChange={(newVal) => setFtDeptFilter(newVal)}
                            options={ftDeptOptions}
                            value={ftDeptFilter}
                          />
                        </LabelWrap>
                        <FilterButton2
                          disabled={ftFilterCount === 0}
                          onClick={() => {
                            setFtCountyFilter(new Set());
                            setFtTeamFilter(new Set());
                            setFtDeptFilter(new Set());
                          }}
                        >
                          Rensa
                        </FilterButton2>
                      </FilterDropdownWrapper>
                    )
                  }
                >
                  <FilterButton2
                    icon={<FontAwesomeIcon icon={faFilter} />}
                    onClick={() => setFtFiltersOpen(!ftFiltersOpen)}
                  >
                    <span>Filter</span>
                    {ftFilterCount > 0 && (
                      <FilterBadge>{ftFilterCount}</FilterBadge>
                    )}
                  </FilterButton2>
                </Dropdown>
              </FilterBar>

              <Table
                columnSettings={ftColumnSettings}
                rows={filteredFieldTesters}
                useColumnWidthAsFlexAmount
              />
            </>
          )}
        </>
      )}
    </Container>
  );
};

export default DayPlanningOverview;
